import React from "react";
import InputLabel from "./InputLabel";
import ErrorMessage from "../ErrorMessages/ErrorMessage";
import { getMaxLength } from "../commonfunctions/getMaxLength";
import useTranslation from "../CustomHooks/useTranslation";
import { inputFieldContent } from "./InputFieldContent";
import InputShimmer from "./InputShimmer";

interface InputItem {
  labelName: string;
  id: string;
}

interface InputFieldProps {
  inputItem: InputItem;
  errorMessage?: string;
  required?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  fieldError?: string | boolean;
  tabIndex: number; // Total number of input fields to determine navigation
  maxLength?: number;
  testId?: string;
  disabled?: boolean;
  value?: string;
  type?: string;
  className?: string;
  maindivstyle?: string;
  editLoading?: boolean;
  placeholder?: string;
  secondaryText?: string;
  percentage?:boolean;
  customWidth?: boolean;
}

const InputField: React.FC<InputFieldProps> = ({
  inputItem,
  errorMessage,
  onChange,
  required,
  fieldError,
  maxLength,
  testId,
  disabled,
  value,
  className,
  maindivstyle,
  editLoading,
  placeholder,
  secondaryText,
  customWidth,
}) => {
  const { labelName, id } = inputItem;
  const { translate } = useTranslation(inputFieldContent);

  // Handle change event with max length restriction
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const maxLength = getMaxLength(id);
    let { value } = event.target;

    if (id === "email_address" || id === "official_email_address") {
      // Remove any spaces from the email address
      value = value.replace(/\s/g, ""); // This removes all spaces
    }

    // Restrict the input value to the max length
    if (maxLength !== undefined && value.length > maxLength) {
      value = value.slice(0, maxLength);
    }
    // Update the value of the input field
    event.target.value =
      value.trim().length === 0 ? "" : value.split("  ").join(" ");
    if (onChange) {
      onChange(event); // Trigger the parent onChange handler
    }
  };


  return (
    <div className={`${maindivstyle}`}>
      <InputLabel labelName={labelName} id={id} required={required} />
      {editLoading ? (
        <InputShimmer className={className} />
      ) : (
        <>
          <input
            id={id}
            data-testid={testId || id}
            className={`${className} disabled:cursor-not-allowed ${customWidth ? 'w-full' : 'w-80'} h-14 py-5 pr-[15px] pl-2.5 focus:pl-[9px] disabled:bg-disable_fill disabled:border-disable_stroke border rounded-lg text-subTitle outline-none focus:border-2 ${errorMessage || fieldError
                ? "border-negative"
                : "border-P_light_active"
              }`}
            placeholder={placeholder ? translate(placeholder) : translate(`Enter ${labelName.toLocaleLowerCase()}`)}
            onChange={handleChange} // Use the updated onChange handler
            // onKeyDown={handleKeyDown} // Handle arrow key navigation
            // tabIndex={tabIndex + 1} // Ensure correct tab order for navigation
            autoComplete="off"
            maxLength={maxLength}
            disabled={disabled}
            value={value}
          />
          {(errorMessage && !disabled) && <div className="mb-1"><ErrorMessage errorMessage={errorMessage} testId={`error-${testId || id}`}/></div>}
          {secondaryText && <p className="mt-1 text-body2 text-secondary_text">{secondaryText}</p>}
        </>
      )}
    </div>
  );
};

export default InputField;
